import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Typography, Divider, Space } from 'antd';
import {
  WechatOutlined, CustomerServiceOutlined, TeamOutlined,
  MailOutlined, PhoneOutlined, ClockCircleOutlined, CloseOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

/** 企业微信客服/加群入口（全站统一） */
const WEWORK_KF_URL = 'https://work.weixin.qq.com/kfid/kfce20d584b0179916f';
const SERVICE_PHONE = '13599530881';
const SERVICE_EMAIL = 'xmwcg5059@outlook.com';
const SERVICE_TIME = '周一至周日 9:00 – 21:00（北京时间）';

/**
 * 全站统一「客服 / 加群」弹窗
 * 所有客服链接、加群链接点击后先弹出本弹窗（含企业微信客服、个人微信、加群、飞书、邮箱、热线），
 * 弹窗带右上角 X 与底部「关闭」按钮。
 * 用法：任意组件 import { triggerContactService } 即可打开。
 */
type ContactEvent = {};

const listeners = new Set<(e: ContactEvent) => void>();

export function triggerContactService(e: ContactEvent = {}) {
  listeners.forEach((fn) => fn(e));
}

export function ContactServiceProvider({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(false);

  const show = useCallback(() => {
    setVisible(true);
    ref.current = true;
  }, []);

  useEffect(() => {
    listeners.add(show);
    return () => {
      listeners.delete(show);
    };
  }, [show]);

  const close = useCallback(() => setVisible(false), []);

  return (
    <>
      {children}
      <Modal
        open={visible}
        onCancel={close}
        title={
          <Space>
            <CustomerServiceOutlined style={{ color: '#07c160' }} />
            联系客服 / 加入交流群
          </Space>
        }
        footer={[
          <Button key="close" icon={<CloseOutlined />} onClick={close}>
            关闭
          </Button>,
        ]}
        width={420}
        centered
        closable
      >
        <div style={{ textAlign: 'center' }}>
          <Paragraph type="secondary" style={{ marginBottom: 14 }}>
            选择您方便的方式，专业顾问为您服务
          </Paragraph>

          <Button
            type="primary"
            icon={<WechatOutlined />}
            block
            size="large"
            onClick={() => window.open(WEWORK_KF_URL, '_blank', 'noopener,noreferrer')}
            style={{ marginBottom: 10, background: '#07c160', borderColor: '#07c160', borderRadius: 10, height: 44, fontWeight: 600 }}
          >
            打开企业微信客服
          </Button>

          <Button
            icon={<TeamOutlined />}
            block
            size="large"
            onClick={() => window.open(WEWORK_KF_URL, '_blank', 'noopener,noreferrer')}
            style={{ marginBottom: 14, borderRadius: 10, height: 44, fontWeight: 600, color: '#07c160', borderColor: '#07c160' }}
          >
            加入官方交流群
          </Button>

          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            扫码添加个人微信（备注「AIbak」）
          </Paragraph>
          <img
            src="/wechat-qr.png"
            alt="个人微信二维码"
            style={{ width: 168, height: 168, borderRadius: 10, border: '1px solid var(--border)', background: '#fff' }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />

          <Divider style={{ margin: '16px 0 10px' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, textAlign: 'left', color: 'var(--text-secondary)' }}>
            <Space>
              <CustomerServiceOutlined style={{ color: '#3370ff' }} />
              <span>飞书客服：搜索飞书企业「AIbak」</span>
            </Space>
            <Space>
              <MailOutlined style={{ color: '#6c5ce7' }} />
              <span>商务 / 技术邮箱：<b style={{ color: 'var(--text-primary)' }}>{SERVICE_EMAIL}</b></span>
            </Space>
            <Space>
              <PhoneOutlined style={{ color: '#07c160' }} />
              <span>服务热线：<b style={{ color: 'var(--text-primary)' }}>{SERVICE_PHONE}</b></span>
            </Space>
            <Space>
              <ClockCircleOutlined style={{ color: '#f59e0b' }} />
              <span>在线时间：{SERVICE_TIME}</span>
            </Space>
          </div>
        </div>
      </Modal>
    </>
  );
}
